import React, { useState, useEffect, useCallback, useRef } from 'react';
import { verifyMyPassword } from './lib/verifyPassword';
import { useApp } from './context';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import {
  Building2, Plus, ChevronRight, ChevronLeft, Trash2, Users,
  BookOpen, Save, X, Layers, UserPlus, Upload, CheckCircle, XCircle,
  Mail, Hash, ClipboardCopy, AlertCircle, GraduationCap, Sparkles,
  FileText, Settings, Lock, Eye, EyeOff, Shield, LogOut,
  LayoutDashboard, RefreshCw, Search, Menu, Info,
  Calendar, Key, Briefcase, Command, ArrowRight, Library
} from './components/DeskeyIcons';
import { toast } from 'sonner';
import { DASHBOARD_THEMES } from './lib/themes';
import { TeachersSection } from './components/dashboard/TeachersSection';
import { StudentsSection } from './components/dashboard/StudentsSection';
import { TeacherDeskSection } from './components/dashboard/TeacherDeskSection';
import { DeskeyHubSection } from './components/dashboard/DeskeyHubSection';
import { ClassSubjectsTab } from './SubjectManagement';
import { ProfileCard } from './components/ProfileCard';
import { notifyError, friendlyError } from './lib/errors';
import { confirmDialog } from './components/ConfirmDialog';
import Loader from './components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_PASSWORD_LENGTH = 8;
// Class and batch codes are 3 letters + 4 digits (e.g. PHY4821); a class code doubles as the student join code.
const CODE_RE = /^[A-Z]{3}[0-9]{4}$/;
const CODE_CHECK_DELAY_MS = 350;
const BATCH_YEAR_SPAN = 10; // how far the From/To year pickers reach
const academicYear = (from, to) => `${from}-${String(to).slice(-2)}`;

// Live availability check for a code the admin typed themselves
// (kind = 'classes' | 'batches'). null while untouched or empty.
function useCodeCheck(kind, code, touched, active) {
  const [status, setStatus] = useState(null); // null | checking | available | taken | invalid
  useEffect(() => {
    const trimmed = code.trim();
    if (!active || !touched || !trimmed) { setStatus(null); return undefined; }
    if (!CODE_RE.test(trimmed)) { setStatus('invalid'); return undefined; }
    setStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const r = await api(`/department/${kind}/check-code?code=${encodeURIComponent(trimmed)}`);
        setStatus(r?.available ? 'available' : 'taken');
      } catch { setStatus(null); }
    }, CODE_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [kind, code, touched, active]);
  return status;
}

const codeBlocks = (status) => status === 'taken' || status === 'invalid' || status === 'checking';

function CodeStatus({ status, touched, code, hint }) {
  if (status === 'checking') return <p className="text-[11px] text-gray-400 mt-1">Checking…</p>;
  if (status === 'available') return <p className="text-[11px] text-emerald-600 mt-1">Available</p>;
  if (status === 'taken') return <p className="text-[11px] text-red-500 mt-1" data-testid="code-taken">Already in use</p>;
  if (status === 'invalid') return <p className="text-[11px] text-red-500 mt-1" data-testid="code-invalid">Use 3 letters + 4 digits, e.g. PHY4821</p>;
  if (!touched && code) return <p className="text-[11px] text-gray-400 mt-1">{hint}</p>;
  return null;
}

function api(path, opts = {}) {
  const token = localStorage.getItem('desky_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers };
  return fetch(`${API_URL}/api${path}`, { headers, ...opts }).then(r => r.json());
}

// Like api(), but for the shared /classes and /class-substitutions endpoints:
// throws with the server's message instead of resolving on a 4xx.
async function apiOrThrow(path, opts = {}) {
  const token = localStorage.getItem('desky_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers };
  const res = await fetch(`${API_URL}/api${path}`, { headers, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(friendlyError(data, 'Request failed')), { data });
  return data;
}

const DeskyLogo = () => (
  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #34A853, #2E9A4B)' }}>
    <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
      <path d="M8 6H18C23.5228 6 28 10.4772 28 16C28 21.5228 23.5228 26 18 26H8V6Z" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
      <circle cx="18" cy="16" r="4" fill="white" fillOpacity="0.9" />
      <path d="M8 16H14" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  </div>
);

const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-5 transition-all duration-200 ${className}`} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', ...props.style }} {...props}>{children}</div>
);

const MENU = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'classes', label: 'Classes & Batch', icon: Layers },
  { id: 'teacher-desk', label: 'Teacher Desk', icon: FileText },
  { id: 'deskey-hub', label: 'DeskeyHUB', icon: Library },
  { id: 'teachers', label: 'Teachers', icon: Briefcase },
  { id: 'students', label: 'Students', icon: GraduationCap },
  // Enrollment Requests page hidden for now — pending requests are handled in each class's "Students Applied" tab.
  // { id: 'enrollments', label: 'Enrollment Requests', icon: UserPlus },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const DEPT_SEARCH_ITEMS = [
  { id: 'overview', label: 'Overview', desc: 'Department stats', icon: LayoutDashboard, type: 'page' },
  { id: 'classes', label: 'Classes', desc: 'Manage classes & students', icon: Layers, type: 'page' },
  { id: 'teacher-desk', label: 'Teacher Desk', desc: 'Published lesson notes log', icon: FileText, type: 'page' },
  { id: 'deskey-hub', label: 'DeskeyHUB', desc: 'Department library', icon: Library, type: 'page' },
  { id: 'teachers', label: 'Teachers', desc: 'View department teachers', icon: Briefcase, type: 'page' },
  { id: 'students', label: 'Students', desc: 'All enrolled students', icon: GraduationCap, type: 'page' },
  // { id: 'enrollments', label: 'Enrollment Requests', desc: 'Approve or reject enrollments', icon: UserPlus, type: 'page' }, // Enrollment Requests page hidden for now
  { id: 'settings', label: 'Settings', desc: 'Department settings', icon: Settings, type: 'page' },
  { id: 'action_new_class', label: 'Create New Class', desc: 'Open a batch, then add a class', icon: Plus, type: 'action', navigateTo: 'classes' },
];

function DeptSpotlightSearch({ onNavigate, accent }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const A = accent;

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open) { setQuery(''); setActiveIdx(0); }
  }, [open]);

  const filtered = query.trim()
    ? DEPT_SEARCH_ITEMS.filter(i => i.label.toLowerCase().includes(query.toLowerCase()) || i.desc.toLowerCase().includes(query.toLowerCase()))
    : DEPT_SEARCH_ITEMS.filter(i => i.type === 'page');

  const handleSelect = (item) => { onNavigate(item.navigateTo || item.id); setOpen(false); setQuery(''); };
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && filtered[activeIdx]) handleSelect(filtered[activeIdx]);
  };
  useEffect(() => { setActiveIdx(0); }, [query]);

  return (
    <>
      {/* Trigger — hidden when modal is open */}
      {!open && (
        <button onClick={() => setOpen(true)} className="hidden sm:flex items-center gap-2.5 h-9 pl-3.5 pr-3 rounded-xl border border-gray-200 bg-gray-50/60 hover:bg-gray-100 hover:border-gray-300 transition-all text-sm text-gray-500 min-w-[220px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]" data-testid="spotlight-trigger">
          <Search className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
          <span className="text-[13px] flex-1 text-left text-gray-500/80" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Search...</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] text-gray-500 bg-white border border-gray-200 rounded-md px-1.5 py-0.5 font-mono shadow-[0_1px_0_rgba(0,0,0,0.04)]"><Command className="w-2.5 h-2.5" />K</kbd>
        </button>
      )}
      {!open && (
        <button onClick={() => setOpen(true)} className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"><Search className="w-4 h-4 text-gray-500" strokeWidth={1.75} /></button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" data-testid="spotlight-modal">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 border border-gray-200">
            <div className="flex items-center gap-3 px-4 h-14 border-b border-gray-100">
              <Search className="w-5 h-5 text-gray-500 flex-shrink-0" strokeWidth={1.75} />
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="Search pages, actions..." className="flex-1 text-[15px] text-gray-900 placeholder:text-gray-500 outline-none bg-transparent" data-testid="spotlight-input" />
              <button onClick={() => { setOpen(false); setQuery(''); }} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0" data-testid="spotlight-close">
                <X className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
              </button>
            </div>
            <div className="max-h-[340px] overflow-y-auto py-2">
              {filtered.length === 0 ? (
                <div className="px-4 py-10 text-center"><Search className="w-8 h-8 text-gray-200 mx-auto mb-2" /><p className="text-sm text-gray-500">No results</p></div>
              ) : filtered.map((item, i) => (
                <button key={item.id} onClick={() => handleSelect(item)} onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? 'bg-gray-50' : ''}`} data-testid={`spotlight-item-${item.id}`}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={i === activeIdx ? { background: `${A}14` } : { background: '#f1f5f980' }}>
                    <item.icon className="w-[17px] h-[17px]" style={{ color: i === activeIdx ? A : '#94a3b8' }} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0"><p className={`text-[13px] font-medium truncate ${i === activeIdx ? 'text-gray-900' : 'text-gray-800'}`}>{item.label}</p><p className="text-[11px] text-gray-500 truncate">{item.desc}</p></div>
                  {i === activeIdx && <ArrowRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                  {item.type === 'action' && i !== activeIdx && <span className="text-[9px] font-semibold text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 uppercase tracking-wider flex-shrink-0">Action</span>}
                </button>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 font-mono text-[9px]">↑↓</kbd> Navigate</span>
              <span className="flex items-center gap-1"><kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 font-mono text-[9px]">↵</kbd> Open</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function DepartmentDashboard() {
  const { user, logout: ctxLogout } = useApp();

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [section, setSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Classes
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState({ name: '', code: '', capacity: 30, batch_id: '', assigned_teacher_id: '', subject_id: '' });
  const [classCodeTouched, setClassCodeTouched] = useState(false);
  const classCodeStatus = useCodeCheck('classes', classForm.code, classCodeTouched, showClassForm);
  const [formTeachers, setFormTeachers] = useState([]);
  const [formSubjects, setFormSubjects] = useState([]);

  // Batches
  const [batches, setBatches] = useState([]);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [batchForm, setBatchForm] = useState({ name: '', code: '', fromYear: '', toYear: '' });
  const [batchCodeTouched, setBatchCodeTouched] = useState(false);
  const batchCodeStatus = useCodeCheck('batches', batchForm.code, batchCodeTouched, showBatchForm);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [classForBatch, setClassForBatch] = useState(null);
  const [batchSearch, setBatchSearch] = useState('');

  // Students in selected class
  const [classStudents, setClassStudents] = useState([]);
  const [openingClass, setOpeningClass] = useState(false);
  const [pendingEnrollments, setPendingEnrollments] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [addMode, setAddMode] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [csvInput, setCsvInput] = useState('');
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [classTab, setClassTab] = useState('students');
  const [showClassTeacherModal, setShowClassTeacherModal] = useState(false);
  const [ctQuery, setCtQuery] = useState('');
  const [ctResults, setCtResults] = useState([]);
  const [ctSearching, setCtSearching] = useState(false);
  const [ctAssigning, setCtAssigning] = useState(null);
  const [showUnassign2FA, setShowUnassign2FA] = useState(false);
  const [unassignPwd, setUnassignPwd] = useState('');
  const [unassigning, setUnassigning] = useState(false);
  useEffect(() => {
    if (!showClassTeacherModal) return;
    setCtSearching(true);
    const tk = localStorage.getItem('desky_token');
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/teachers/search?q=${encodeURIComponent(ctQuery)}`, { headers: { Authorization: `Bearer ${tk}` } }).then(r => r.json()).then(d => { setCtResults(d || []); setCtSearching(false); }).catch(() => setCtSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [showClassTeacherModal, ctQuery]);
  const [teacherInfoPopup, setTeacherInfoPopup] = useState(null);
  // Class substitute state
  const [showSubModal, setShowSubModal] = useState(false);
  const [subStep, setSubStep] = useState(1);
  const [subQuery, setSubQuery] = useState('');
  const [subResults, setSubResults] = useState([]);
  const [subSearching, setSubSearching] = useState(false);
  const [subSelected, setSubSelected] = useState(null);
  const [subStartDate, setSubStartDate] = useState('');
  const [subEndDate, setSubEndDate] = useState('');
  const [subReason, setSubReason] = useState('');
  const [subConfirmPwd, setSubConfirmPwd] = useState('');
  const [subAssigning, setSubAssigning] = useState(false);
  const [subError, setSubError] = useState('');
  const [classSubs, setClassSubs] = useState([]);
  const [showUnassignSub2FA, setShowUnassignSub2FA] = useState(null);
  const [unassignSubPwd, setUnassignSubPwd] = useState('');
  const [unassigningSub, setUnassigningSub] = useState(false);

  // All students / all pending (cross-class)
  const [allStudents, setAllStudents] = useState([]);
  // eslint-disable-next-line no-unused-vars -- read only by the Enrollment Requests page, hidden for now
  const [allPending, setAllPending] = useState([]);

  // Department teachers (read-only)
  const [deptTeachers, setDeptTeachers] = useState([]);

  // Student profile modal
  const [selectedStudentProfile, setSelectedStudentProfile] = useState(null);

  // Class detail student search
  const [classStudentSearch, setClassStudentSearch] = useState('');

  // Settings
  // Change password: emailed code first, then current + new password.
  const [pwdCodeSent, setPwdCodeSent] = useState(false);
  const [sendingPwdCode, setSendingPwdCode] = useState(false);
  const [pwdCode, setPwdCode] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  // Theme from branding + dashboard_theme (matches Institution Dashboard logic)
  const tenant = dashboard?.tenant;
  const branding = dashboard?.branding || {};
  const dashTheme = dashboard?.dashboard_theme;

  // Resolve theme: custom colors > preset theme > branding > fallback
  let resolvedAccent = '#00AA8D';
  let resolvedSidebar = '#1a1f36';
  if (dashTheme) {
    if (dashTheme.theme_id === 'custom' && dashTheme.custom_colors) {
      resolvedAccent = dashTheme.custom_colors.accent || resolvedAccent;
      resolvedSidebar = dashTheme.custom_colors.sidebar || resolvedSidebar;
    } else {
      const preset = DASHBOARD_THEMES.find(t => t.id === dashTheme.theme_id);
      if (preset) {
        resolvedAccent = preset.accent;
        resolvedSidebar = preset.sidebar;
      }
    }
  }
  // If no dashboard theme, fall back to branding colors
  if (!dashTheme) {
    if (branding.accent_color) resolvedAccent = branding.accent_color;
    else if (branding.primary_color) resolvedAccent = branding.primary_color;
  }

  const A = resolvedAccent;
  const sidebarBg = resolvedSidebar;
  const logoUrl = branding.logo_url || '';
  const dept = dashboard?.department || {};
  const stats = dashboard?.stats || {};
  const classBreakdown = dashboard?.class_breakdown || [];

  // ─── DATA LOADERS ─────────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    const d = await api('/department/dashboard');
    setDashboard(d);
    // Persist tenant + branding data for sidebar
    if (d.tenant) {
      const merged = { ...d.tenant, branding: d.branding || {}, dashboard_theme: d.dashboard_theme };
      try { localStorage.setItem('desky_tenant', JSON.stringify(merged)); } catch {}
    }
    setLoading(false);
  }, []);

  const loadClasses = useCallback(async () => {
    const d = await api('/department/classes');
    setClasses(d.classes || []);
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      const token = localStorage.getItem('desky_token');
      const res = await fetch(`${API_URL}/api/batches`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBatches(data || []);
    } catch {}
  }, []);

  const loadAllStudents = useCallback(async () => {
    const d = await api('/department/all-students');
    setAllStudents(d.students || []);
  }, []);

  const loadAllPending = useCallback(async () => {
    const d = await api('/department/all-pending');
    setAllPending(d.enrollments || []);
  }, []);

  const loadDeptTeachers = useCallback(async () => {
    const d = await api('/department/teachers');
    setDeptTeachers(d.teachers || []);
  }, []);

  useEffect(() => { loadDashboard(); loadClasses(); loadBatches(); }, [loadDashboard, loadClasses, loadBatches]);
  // Enrollment Requests page hidden for now — restore loadAllPending() here with the sidebar badge.

  // Load section data on section change
  useEffect(() => {
    if (section === 'students') loadAllStudents();
    if (section === 'teachers') loadDeptTeachers();
    if (section === 'enrollments') loadAllPending();
  }, [section, loadAllStudents, loadDeptTeachers, loadAllPending]);

  // Load all tenant teachers for the class-creation form's required Teacher picker.
  useEffect(() => {
    if (!showClassForm) return;
    api('/teachers/search?q=').then(d => setFormTeachers(Array.isArray(d) ? d : [])).catch(() => {});
    api('/subjects').then(d => setFormSubjects(Array.isArray(d) ? d : [])).catch(() => {});
  }, [showClassForm]);

  // ─── CLASS ACTIONS ────────────────────────────────────────────────────
  const classCodeBlocked = codeBlocks(classCodeStatus);

  const saveClass = async () => {
    if (!classForm.name) { toast.error('Class name is required'); return; }
    if (classCodeBlocked) { toast.error('Fix the class code first'); return; }
    if (!classForm.assigned_teacher_id) { toast.error('Assign a teacher to the class'); return; }
    if (!classForm.subject_id) { toast.error('Select exactly one subject for the class'); return; }
    const payload = { ...classForm };
    if (classForBatch) payload.batch_id = classForBatch;
    const res = await api('/department/classes', { method: 'POST', body: JSON.stringify(payload) });
    if (res?.detail) { notifyError(res); return; }
    toast.success('Class created');
    setShowClassForm(false);
    setClassForBatch(null);
    setClassForm({ name: '', code: '', capacity: 30, batch_id: '', assigned_teacher_id: '', subject_id: '' });
    loadClasses(); loadBatches(); loadDashboard();
  };

  const batchYear = batchForm.fromYear && batchForm.toYear ? academicYear(batchForm.fromYear, batchForm.toYear) : '';

  const openBatchForm = async () => {
    const thisYear = new Date().getFullYear();
    setBatchForm({ name: '', code: '', fromYear: String(thisYear), toYear: String(thisYear + 1) });
    setBatchCodeTouched(false);
    setShowBatchForm(true);
    try {
      const r = await api('/department/batches/suggest-code');
      if (r?.code) setBatchForm(p => (p.code ? p : { ...p, code: r.code }));
    } catch { /* best-effort; the server generates one on create anyway */ }
  };

  const saveBatch = async () => {
    if (!batchYear) { toast.error('Pick the academic year'); return; }
    if (codeBlocks(batchCodeStatus)) { toast.error('Fix the batch code first'); return; }
    const res = await api('/department/batches', {
      method: 'POST',
      body: JSON.stringify({ name: batchForm.name.trim(), year: batchYear, code: batchForm.code.trim() }),
    });
    if (res?.detail) { notifyError(res, 'Could not create the batch'); return; }
    toast.success('Batch created');
    setShowBatchForm(false);
    loadBatches();
  };

  const deleteBatch = async (id) => {
    if (!(await confirmDialog({ title: 'Delete batch', message: 'Delete this batch?', danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await apiOrThrow(`/batches/${id}`, { method: 'DELETE' });
    } catch (e) { notifyError(e.data, 'Could not delete the batch'); return; }
    toast.success('Batch deleted');
    loadBatches();
  };

  const deleteClass = async (id) => {
    if (!(await confirmDialog({ title: 'Delete class', message: 'Delete this class and all its data?', danger: true, confirmLabel: 'Delete' }))) return;
    const res = await api(`/department/classes/${id}`, { method: 'DELETE' });
    if (res?.detail) { notifyError(res, 'Could not delete the class'); return; }
    toast.success('Class deleted');
    if (selectedClass?.id === id) setSelectedClass(null);
    loadClasses();
    loadDashboard();
  };

  const openClass = async (cls) => {
    setSelectedClass(cls);
    setClassTab('students');
    setAddMode(null);
    setClassStudents([]);  // never show the previous classroom's students while this one loads
    setPendingEnrollments([]);
    loadClassSubs(cls.id);
    setOpeningClass(true);
    try { await Promise.all([loadClassStudents(cls.id), loadPendingEnrollments(cls.id)]); }
    catch (e) { notifyError(e, "Couldn't load students."); }
    setOpeningClass(false);
  };

  const loadClassSubs = async (cid) => {
    try {
      const tk = localStorage.getItem('desky_token');
      const res = await fetch(`${API_URL}/api/classes/${cid}/substitutes`, { headers: { Authorization: `Bearer ${tk}` } });
      const data = await res.json();
      setClassSubs(Array.isArray(data) ? data : []);
    } catch { setClassSubs([]); }
  };

  // ─── PER-CLASS STUDENT MANAGEMENT ─────────────────────────────────────
  const loadClassStudents = async (cid) => { const d = await api(`/department/classes/${cid}/students`); setClassStudents(d.students || []); };
  const loadPendingEnrollments = async (cid) => { const d = await api(`/department/classes/${cid}/pending-enrollments`); setPendingEnrollments(d.enrollments || []); };
  const loadAvailableStudents = async (cid) => { const d = await api(`/department/classes/${cid}/available-students`); setAvailableStudents(d.students || []); };

  // Shared by the email / CSV / pick add modes.
  const submitStudents = async (path, body) => {
    setEnrollLoading(true);
    const r = await api(`/department/classes/${selectedClass.id}/${path}`, { method: 'POST', body: JSON.stringify(body) });
    setEnrollLoading(false);
    if (r?.detail) { notifyError(r, 'Could not add students'); return false; }
    const parts = [];
    if (r.added?.length) parts.push(`${r.added.length} added`);
    if (r.not_found?.length) parts.push(`${r.not_found.length} not found`);
    if (r.already_enrolled?.length) parts.push(`${r.already_enrolled.length} already enrolled`);
    toast.success(parts.join(', ') || 'Done');
    setAddMode(null);
    loadClassStudents(selectedClass.id);
    loadDashboard();
    return true;
  };

  const addStudentsByEmail = async () => {
    if (!emailInput.trim()) return;
    const emails = emailInput.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    if (await submitStudents('add-students', { emails })) setEmailInput('');
  };

  const addStudentsByCSV = async () => {
    if (!csvInput.trim()) return;
    if (await submitStudents('add-students-csv', { csv_text: csvInput })) setCsvInput('');
  };

  const addSelectedStudents = async () => {
    if (!selectedAvailable.length) return;
    if (await submitStudents('add-students', { emails: selectedAvailable.map(s => s.email) })) setSelectedAvailable([]);
  };

  // Approve / reject from a class's "Students Applied" tab or the cross-class list.
  const handleEnrollmentAction = async (classId, enrollmentId, action) => {
    const r = await api(`/department/classes/${classId}/enrollment-action`, { method: 'POST', body: JSON.stringify({ enrollment_id: enrollmentId, action }) });
    if (r?.detail) { notifyError(r, `Could not ${action} the request`); return; }
    toast.success(`Enrollment ${action}d`);
    if (selectedClass?.id === classId) {
      loadPendingEnrollments(classId);
      if (action === 'approve') loadClassStudents(classId);
    }
    loadAllPending();
    loadDashboard();
  };

  const removeStudent = async (studentId) => {
    if (!(await confirmDialog({ title: 'Remove student', message: 'Remove this student?', danger: true, confirmLabel: 'Remove' }))) return;
    const r = await api(`/department/classes/${selectedClass.id}/remove-student`, { method: 'POST', body: JSON.stringify({ student_id: studentId }) });
    if (r?.detail) { notifyError(r, 'Could not remove the student'); return; }
    toast.success('Student removed');
    loadClassStudents(selectedClass.id);
    loadDashboard();
  };

  // ─── PASSWORD ACTIONS ────────────────────────────────────────────────
  const sendPasswordCode = async () => {
    setSendingPwdCode(true);
    const r = await api('/department/change-password/send-code', { method: 'POST' });
    setSendingPwdCode(false);
    if (!r?.sent) { notifyError(r, 'Could not send the code'); return; }
    setPwdCodeSent(true);
    setPwdCode('');
    toast.success(`Code sent to ${r.email}`);
  };

  const resetPasswordForm = () => {
    setPwdCodeSent(false); setPwdCode('');
    setOldPassword(''); setNewPassword('');
  };

  const changePassword = async () => {
    if (pwdCode.length !== 6 || !oldPassword || newPassword.length < MIN_PASSWORD_LENGTH) return;
    setChangingPwd(true);
    const r = await api('/department/change-password', { method: 'POST', body: JSON.stringify({ code: pwdCode, current_password: oldPassword, new_password: newPassword }) });
    setChangingPwd(false);
    if (r.message) { toast.success('Password changed'); resetPasswordForm(); }
    else notifyError(r, 'Could not change the password');
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success('Copied!'); };

  // Context logout also frees this device's slot in the login device limit.
  const doLogout = async () => {
    if (!(await confirmDialog({ title: 'Sign out', message: 'Are you sure you want to sign out?', confirmLabel: 'Sign out' }))) return;
    ctxLogout();
    window.location.href = '/mydeskey';
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader />
    </div>
  );

  // Classes are always created inside a batch.
  const openClassForm = async (batchId) => {
    setClassForBatch(batchId);
    setClassForm({ name: '', code: '', capacity: 30, batch_id: batchId || '', assigned_teacher_id: '', subject_id: '' });
    setClassCodeTouched(false);
    setShowClassForm(true);
    try {
      const r = await api('/department/classes/suggest-code');
      if (r?.code) setClassForm(p => (p.code ? p : { ...p, code: r.code }));
    } catch { /* best-effort; the server generates one on create anyway */ }
  };
  const closeClassForm = () => { setShowClassForm(false); setClassForBatch(null); };

  // New-class form — shown inside a batch and on the Classes page (called as
  // a function, not a component, to keep input focus).
  const renderClassForm = (title) => (
    <Card className="border-2 border-dashed" style={{ borderColor: `${A}40` }} data-testid="class-form">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-base font-bold text-gray-900">{title}</h4>
        <button onClick={closeClassForm} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className="block text-xs font-medium text-gray-500 mb-1">Name</label><Input value={classForm.name} onChange={e => setClassForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Physics 11-A" className="h-10 rounded-xl text-sm" data-testid="class-name-input" /></div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Code</label>
          <Input value={classForm.code} onChange={e => { setClassCodeTouched(true); setClassForm(p => ({ ...p, code: e.target.value.toUpperCase() })); }} placeholder="Generating…" maxLength={7} className="h-10 rounded-xl text-sm font-mono uppercase" data-testid="class-code-input" />
          <CodeStatus status={classCodeStatus} touched={classCodeTouched} code={classForm.code} hint="Students use this code to join" />
        </div>
        <div><label className="block text-xs font-medium text-gray-500 mb-1">Capacity</label><Input type="number" value={classForm.capacity} onChange={e => setClassForm(p => ({ ...p, capacity: parseInt(e.target.value) || 30 }))} className="h-10 rounded-xl text-sm" /></div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Teacher <span className="text-red-500">*</span></label>
          <select value={classForm.assigned_teacher_id} onChange={e => setClassForm(p => ({ ...p, assigned_teacher_id: e.target.value }))} className="h-10 w-full rounded-xl text-sm border border-gray-200 px-3 bg-white" data-testid="class-teacher-select">
            <option value="">Select teacher…</option>
            {formTeachers.map(t => <option key={t.id} value={t.id}>{t.full_name || t.email}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject <span className="text-red-500">*</span></label>
          <select value={classForm.subject_id} onChange={e => setClassForm(p => ({ ...p, subject_id: e.target.value }))} className="h-10 w-full rounded-xl text-sm border border-gray-200 px-3 bg-white" data-testid="class-subject-select">
            <option value="">Select subject…</option>
            {formSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Every class must have an assigned teacher and exactly one subject.</p>
      <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
        <Button onClick={saveClass} disabled={!classForm.name || !classForm.assigned_teacher_id || !classForm.subject_id || classCodeBlocked} className="h-10 px-6 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: A }} data-testid="save-class-btn"><Save className="w-4 h-4 mr-1.5" /> Create</Button>
        <Button variant="ghost" onClick={closeClassForm} className="h-10 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
      </div>
    </Card>
  );

  // Class teacher changes go through the shared /classes endpoints.
  const reloadSelectedClass = async () => {
    const cls = await apiOrThrow(`/classes/${selectedClass.id}`);
    if (cls.id) setSelectedClass(cls);
    loadClasses();
  };

  // Filtered class detail students
  const filteredClassStudents = (() => {
    if (!classStudentSearch.trim()) return classStudents;
    const q = classStudentSearch.toLowerCase();
    return classStudents.filter(s => (s.student_name || '').toLowerCase().includes(q) || (s.student_email || '').toLowerCase().includes(q));
  })();

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="department-dashboard">
      {/* ── SIDEBAR ── */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-[260px] flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ background: sidebarBg }} data-testid="dept-sidebar">
        <div className="px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5 mb-7">
            <DeskyLogo />
            <span className="text-[18px] font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.03em' }}>Deskey</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: A }}>
              <span className="text-[16px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{dept.name?.charAt(0)}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[14px] text-white truncate leading-none" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>{dept.name}</p>
              <p className="text-[10px] text-white/30 truncate leading-none mt-1.5 font-medium">{tenant?.name}</p>
            </div>
          </div>
        </div>

        <div className="mx-5 border-t border-white/[0.06]" />

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {MENU.map(m => {
            const active = section === m.id;
            return (
              <button key={m.id} data-testid={`nav-${m.id}`} onClick={() => { setSection(m.id); setSelectedClass(null); setSelectedBatch(null); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-[9px] rounded-lg text-[13px] transition-all duration-150 ${active ? 'bg-white/[0.12] text-white font-semibold' : 'text-white/70 hover:bg-white/[0.06] hover:text-white/90'}`}>
                <m.icon className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={active ? 2 : 1.5} />
                <span className="truncate">{m.label}</span>
                {/* Enrollment Requests page hidden for now: {m.id === 'enrollments' && allPending.length > 0 && <span className="ml-auto bg-rose-500/90 text-white text-[10px] font-bold px-[7px] py-[2px] rounded-full leading-tight">{allPending.length}</span>} */}
              </button>
            );
          })}
        </nav>

        {/* Bottom: user + logout */}
        <div className="p-4 border-t border-white/[0.08]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white bg-white/[0.1] flex-shrink-0">
              {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-white/90 truncate">{user?.full_name}</p>
              <p className="text-[10px] text-white/30 truncate">{user?.email}</p>
            </div>
            <button onClick={doLogout} className="p-1.5 rounded-lg hover:bg-white/[0.08] transition-colors" data-testid="sidebar-logout" title="Sign out">
              <LogOut className="w-[15px] h-[15px] text-white/30 hover:text-white/60" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 lg:ml-[260px]">
        <header className="sticky top-0 z-20 bg-white/98 backdrop-blur-md border-b border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.06)]" data-testid="main-header">
          <div className="h-[56px] px-5 lg:px-7 flex items-center justify-between gap-4">
            {/* Left — Breadcrumbs */}
            <div className="flex items-center gap-3 min-w-0">
              <button className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 -ml-1 flex-shrink-0" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <button onClick={() => { setSection('overview'); setSelectedClass(null); }} className="hidden sm:flex items-center gap-1.5 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 group" data-testid="breadcrumb-root">
                <Layers className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-[12px] font-medium group-hover:underline underline-offset-2">{dept.name}</span>
              </button>
              <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0 hidden sm:block" />
              {selectedClass ? (
                <div className="flex items-center gap-2 min-w-0">
                  <button onClick={() => { setSelectedClass(null); setClassStudentSearch(''); }} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 transition-colors group flex-shrink-0" data-testid="breadcrumb-classes">
                    <Layers className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="text-[12px] font-medium group-hover:underline underline-offset-2">Classes</span>
                  </button>
                  <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                  <h2 className="text-[15px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>{selectedClass.name}</h2>
                </div>
              ) : (
                (() => {
                  const activeMenu = MENU.find(m => m.id === section);
                  const Icon = activeMenu?.icon;
                  return (
                    <div className="flex items-center gap-2 min-w-0">
                      {Icon && <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${A}10` }}><Icon className="w-3.5 h-3.5" style={{ color: A }} strokeWidth={2} /></div>}
                      <h2 className="text-[15px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>{activeMenu?.label}</h2>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Right — Search, Refresh, User */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <DeptSpotlightSearch onNavigate={(id) => { setSection(id); setSelectedClass(null); }} accent={A} />
              <div className="w-px h-5 bg-gray-200/70 hidden sm:block" />
              <button onClick={() => { loadDashboard(); loadClasses(); }} className="p-[7px] hover:bg-gray-100 rounded-lg transition-all active:scale-95" data-testid="refresh-btn" title="Refresh">
                <RefreshCw className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
              </button>
              <div className="w-px h-5 bg-gray-200/70 hidden sm:block" />
              <div className="hidden sm:flex items-center gap-2.5">
                <div className="text-right">
                  <p className="text-[12px] font-semibold text-gray-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.full_name}</p>
                  <p className="text-[10px] text-gray-500 leading-tight font-medium">Dept Admin</p>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 ring-2 ring-white shadow" style={{ background: A }} data-testid="user-avatar">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              </div>
              {logoUrl && (
                <>
                  <div className="w-px h-5 bg-gray-200/70 hidden sm:block" />
                  <div className="h-8 w-8 rounded-full overflow-hidden border border-gray-200 flex-shrink-0 shadow">
                    <img src={logoUrl} alt="" className="w-full h-full object-contain p-0.5" />
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className={`p-6 md:p-8 max-w-6xl ${['teachers','students','teacher-desk'].includes(section) ? 'overflow-hidden' : ''}`}>

          {/* ═══ OVERVIEW ═══ */}
          {section === 'overview' && !selectedClass && (
            <div className="space-y-7" data-testid="overview-section">
              {/* Greeting */}
              <div>
                <h1 className="text-[22px] font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>Welcome back, {user?.full_name?.split(' ')[0]}</h1>
                <p className="text-sm text-gray-500 mt-1 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Department overview for <span className="font-bold text-gray-700">{dept.name}</span></p>
              </div>

              {/* Stat Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: 'Classes', val: stats.total_classes || 0, icon: Layers, c: A, click: () => setSection('classes') },
                  { label: 'Enrolled Students', val: stats.total_students || 0, icon: GraduationCap, c: '#3B82F6', click: () => setSection('students') },
                  { label: 'Pending Enrollment', val: stats.pending_enrollments || 0, icon: AlertCircle, c: (stats.pending_enrollments || 0) > 0 ? '#DC2626' : '#6B7280', click: () => setSection('classes') }, // Enrollment Requests page hidden for now
                  { label: 'AI Lessons', val: stats.total_lessons || 0, icon: Sparkles, c: '#8B5CF6' },
                  { label: 'Materials', val: stats.total_materials || 0, icon: FileText, c: A },
                  { label: 'Teachers', val: stats.total_teachers || 0, icon: BookOpen, c: '#F59E0B', click: () => setSection('teachers') },
                ].map((c, i) => (
                  <div key={i} onClick={c.click} className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 group" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${c.c}14` }}>
                        <c.icon className="w-[22px] h-[22px]" style={{ color: c.c }} />
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-[28px] font-extrabold text-gray-900 tabular-nums leading-none" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>{c.val}</p>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{c.label}</p>
                  </div>
                ))}
              </div>

              {/* Pending Alert */}
              {(stats.pending_enrollments || 0) > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-red-100/50 transition-colors" onClick={() => setSection('classes')} data-testid="pending-alert" style={{ boxShadow: '0 1px 3px rgba(220,38,38,0.1)' }}>
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-red-900">{stats.pending_enrollments} enrollment{stats.pending_enrollments > 1 ? 's' : ''} pending approval</p>
                    <p className="text-xs text-red-600 font-medium mt-0.5">Open a class → Students Applied to review them</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-red-400" />
                </div>
              )}

              {/* Two Column: Department Info + Quick Actions */}
              <div className="grid lg:grid-cols-5 gap-5">
                {/* Department Details */}
                <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <h3 className="text-[15px] font-extrabold text-gray-900 mb-5" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>Department Details</h3>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    {[
                      { label: 'Department', value: dept.name, icon: Layers },
                      { label: 'Institution', value: tenant?.name, icon: Building2 },
                      { label: 'Code', value: dept.code, icon: Key, mono: true },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{item.label}</p>
                          <span className={`text-sm font-bold text-gray-900 truncate block mt-0.5 ${item.mono ? 'font-mono' : ''}`}>{item.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="lg:col-span-2 space-y-4">
                  {[
                    { title: 'Create New Class', desc: 'Open a batch, then add a class', icon: Plus, color: A, action: () => { setSection('classes'); setSelectedBatch(null); } },
                    // Enrollment Requests page hidden for now
                    // { title: 'Enrollment Requests', desc: (stats.pending_enrollments || 0) > 0 ? `${stats.pending_enrollments} students waiting` : 'No pending requests', icon: UserPlus, color: '#F59E0B', action: () => setSection('enrollments') },
                    { title: 'Teacher Desk', desc: 'View teacher activity & content', icon: Briefcase, color: '#8B5CF6', action: () => setSection('teacher-desk') },
                    { title: 'Settings', desc: 'Department configuration', icon: Settings, color: '#6B7280', action: () => setSection('settings') },
                  ].map((act, i) => (
                    <div key={i} onClick={act.action} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 group" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${act.color}14` }}>
                        <act.icon className="w-5 h-5" style={{ color: act.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900">{act.title}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-medium">{act.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-500 transition-colors" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Class Breakdown Table */}
              {classBreakdown?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-[15px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>Class Breakdown</h3>
                    <button onClick={() => setSection('classes')} className="text-xs font-bold hover:underline" style={{ color: A }} data-testid="view-all-classes">View All</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider pb-3 pl-1">Class</th>
                          <th className="text-right text-[10px] font-bold text-gray-400 uppercase tracking-wider pb-3">Students</th>
                          <th className="text-right text-[10px] font-bold text-gray-400 uppercase tracking-wider pb-3">Pending</th>
                          <th className="text-right text-[10px] font-bold text-gray-400 uppercase tracking-wider pb-3 pr-1">Lessons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classBreakdown.map((c, i) => (
                          <tr key={c.id || i} onClick={() => { const cls = classes.find(x => x.id === c.id); if (cls) { setSection('classes'); openClass(cls); } }} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer" data-testid={`breakdown-row-${c.id}`}>
                            <td className="py-3 pl-1">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${A}14` }}>
                                  <BookOpen className="w-3.5 h-3.5" style={{ color: A }} />
                                </div>
                                <div>
                                  <span className="text-sm font-bold text-gray-900">{c.name}</span>
                                  {c.code && <span className="text-[10px] text-gray-400 font-mono ml-2">{c.code}</span>}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 text-right"><span className="text-sm font-extrabold text-gray-900 tabular-nums">{c.students}</span></td>
                            <td className="py-3 text-right">
                              {c.pending > 0 ? (
                                <span className="text-sm font-bold text-red-600 tabular-nums">{c.pending}</span>
                              ) : (
                                <span className="text-sm font-bold text-gray-400 tabular-nums">0</span>
                              )}
                            </td>
                            <td className="py-3 text-right pr-1"><span className="text-sm font-extrabold text-gray-900 tabular-nums">{c.lessons}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ CLASSES & BATCH ═══ */}
          {section === 'classes' && !selectedClass && !selectedBatch && (
            <div className="space-y-4" data-testid="classes-section">
              {/* Header row */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={batchSearch} onChange={e => setBatchSearch(e.target.value)} placeholder="Search batches..." className="h-9 pl-9 rounded-lg text-sm" data-testid="batch-search" />
                </div>
                <span className="text-[11px] text-gray-400">{batches.length} batch{batches.length !== 1 ? 'es' : ''}</span>
                <div className="ml-auto flex gap-2">
                  <Button onClick={openBatchForm} className="h-9 px-4 rounded-lg text-white text-[13px] font-semibold" style={{ background: A }} data-testid="create-batch-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> New Batch
                  </Button>
                </div>
              </div>

              {/* Create batch inline */}
              {showBatchForm && (
                <Card className="border-2 border-dashed" style={{ borderColor: `${A}40` }} data-testid="batch-form">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-base font-bold text-gray-900">New Batch</h4>
                    <button onClick={() => setShowBatchForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                      <Input value={batchForm.name} onChange={e => setBatchForm(p => ({ ...p, name: e.target.value }))} placeholder={batchYear ? `Batch ${batchYear}` : 'e.g. Grade 10'} className="h-10 rounded-xl text-sm" autoFocus data-testid="batch-name-input" />
                      <p className="text-[11px] text-gray-400 mt-1">Leave blank to use the year</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Academic Year <span className="text-red-500">*</span></label>
                      <div className="flex items-center gap-2">
                        <select value={batchForm.fromYear} onChange={e => { const from = e.target.value; setBatchForm(p => ({ ...p, fromYear: from, toYear: Number(p.toYear) > Number(from) ? p.toYear : String(Number(from) + 1) })); }} className="h-10 w-full rounded-xl text-sm border border-gray-200 px-2 bg-white" data-testid="batch-from-year-select">
                          {Array.from({ length: BATCH_YEAR_SPAN * 2 + 1 }, (_, i) => new Date().getFullYear() - BATCH_YEAR_SPAN + i).map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <span className="text-gray-400 text-sm">to</span>
                        <select value={batchForm.toYear} onChange={e => setBatchForm(p => ({ ...p, toYear: e.target.value }))} className="h-10 w-full rounded-xl text-sm border border-gray-200 px-2 bg-white" data-testid="batch-to-year-select">
                          {Array.from({ length: BATCH_YEAR_SPAN }, (_, i) => Number(batchForm.fromYear) + 1 + i).map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Code</label>
                      <Input value={batchForm.code} onChange={e => { setBatchCodeTouched(true); setBatchForm(p => ({ ...p, code: e.target.value.toUpperCase() })); }} placeholder="Generating…" maxLength={7} className="h-10 rounded-xl text-sm font-mono uppercase" data-testid="batch-code-input" />
                      <CodeStatus status={batchCodeStatus} touched={batchCodeTouched} code={batchForm.code} hint="Auto-generated; you can change it" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                    <Button onClick={saveBatch} disabled={!batchYear || codeBlocks(batchCodeStatus)} className="h-10 px-6 rounded-xl text-white text-sm font-semibold disabled:opacity-50" style={{ background: A }} data-testid="save-batch-btn"><Save className="w-4 h-4 mr-1.5" /> Create</Button>
                    <Button variant="ghost" onClick={() => setShowBatchForm(false)} className="h-10 px-4 rounded-xl text-sm text-gray-500">Cancel</Button>
                  </div>
                </Card>
              )}

              {/* Batch grid — compact cards */}
              {(() => {
                const filtered = batches.filter(b => !batchSearch || b.name?.toLowerCase().includes(batchSearch.toLowerCase()));
                return filtered.length === 0 ? (
                  <div className="text-center py-14">
                    <Layers className="w-10 h-10 mx-auto mb-3" style={{ color: `${A}30` }} />
                    <p className="text-sm text-gray-500">{batches.length === 0 ? 'No batches yet' : 'No batches match your search'}</p>
                    {batches.length === 0 && <p className="text-xs text-gray-400 mt-1">Create a batch to organize classes</p>}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {filtered.map(batch => {
                      const bc = classes.filter(c => c.batch_id === batch.id);
                      const students = bc.reduce((a, c) => a + (c.student_count || 0), 0);
                      return (
                        <div key={batch.id} onClick={() => setSelectedBatch(batch)} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer group hover:shadow-md hover:-translate-y-[1px] transition-all relative" data-testid={`batch-card-${batch.id}`}>
                          <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl" style={{ background: A }} />
                          <button onClick={(e) => { e.stopPropagation(); deleteBatch(batch.id); }} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"><Trash2 className="w-3 h-3" /></button>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white mb-2.5" style={{ background: A }}>
                            {batch.name?.[0] || 'B'}
                          </div>
                          <h4 className="text-[13px] font-bold text-gray-900 truncate leading-tight">{batch.name}</h4>
                          {(batch.year || batch.code) && (
                            <p className="text-[10px] text-gray-400 mt-0.5 truncate">{[batch.year, batch.code].filter(Boolean).join(' · ')}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[10px] font-semibold" style={{ color: A }}>{bc.length} class{bc.length !== 1 ? 'es' : ''}</span>
                            <span className="text-[10px] text-gray-400">{students} students</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Unassigned classes */}
              {(() => {
                const unassigned = classes.filter(c => !c.batch_id || !batches.find(b => b.id === c.batch_id));
                if (unassigned.length === 0) return null;
                return (
                  <div className="pt-2">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Unassigned Classes</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {unassigned.map(c => (
                        <div key={c.id} onClick={() => openClass(c)} className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer group hover:shadow-md hover:-translate-y-[1px] transition-all relative" data-testid={`class-card-${c.id}`}>
                          <button onClick={(e) => { e.stopPropagation(); deleteClass(c.id); }} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"><Trash2 className="w-3 h-3" /></button>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2.5" style={{ background: `${A}12` }}><BookOpen className="w-4 h-4" style={{ color: A }} /></div>
                          <h4 className="text-[13px] font-bold text-gray-900 truncate">{c.name}</h4>
                          <p className="text-[10px] text-gray-400 font-mono mt-0.5">{c.class_code || c.code}</p>
                          <span className="text-[10px] font-semibold mt-1.5 block" style={{ color: A }}>{c.student_count || 0} students</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ═══ INSIDE A BATCH — Show classes ═══ */}
          {section === 'classes' && !selectedClass && selectedBatch && (
            <div className="space-y-6" data-testid="batch-detail">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedBatch(null)} className="p-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all active:scale-95"><ChevronLeft className="w-5 h-5" /></button>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white" style={{ background: A }}>{selectedBatch.name?.[0] || 'B'}</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selectedBatch.name}</h3>
                  <p className="text-xs text-gray-400">
                    {selectedBatch.year && <>Academic Year {selectedBatch.year} &middot; </>}
                    {selectedBatch.code && <><span className="font-mono">{selectedBatch.code}</span> &middot; </>}
                    {classes.filter(c => c.batch_id === selectedBatch.id).length} classes in this batch
                  </p>
                </div>
                <div className="ml-auto">
                  <Button onClick={() => openClassForm(selectedBatch.id)} className="h-10 px-5 rounded-xl text-white text-sm font-semibold" style={{ background: A }} data-testid="add-class-in-batch">
                    <Plus className="w-4 h-4 mr-1.5" /> New Class
                  </Button>
                </div>
              </div>

              {showClassForm && renderClassForm(`New Class in ${selectedBatch.name}`)}

              {(() => {
                const bc = classes.filter(c => c.batch_id === selectedBatch.id);
                return bc.length === 0 ? (
                  <Card className="text-center py-16 border-2 border-dashed">
                    <BookOpen className="w-12 h-12 mx-auto mb-3" style={{ color: `${A}40` }} />
                    <p className="text-sm font-medium text-gray-500">No classes in this batch</p>
                    <p className="text-xs text-gray-400 mt-1">Create classes to start managing students and subjects</p>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {bc.map(c => (
                      <Card key={c.id} className="group cursor-pointer hover:shadow-lg hover:-translate-y-[2px] transition-all relative overflow-hidden !p-0" data-testid={`class-card-${c.id}`}>
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button onClick={(e) => { e.stopPropagation(); deleteClass(c.id); }} className="p-2 rounded-xl bg-white shadow border border-gray-100 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                        </div>
                        <div className="h-1.5 w-full" style={{ background: A }} />
                        <div className="p-5" onClick={() => openClass(c)}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${A}12` }}><BookOpen className="w-5 h-5" style={{ color: A }} /></div>
                            <button onClick={(e) => { e.stopPropagation(); copyCode(c.class_code); }} className="flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-1 rounded-lg font-mono" style={{ background: `${A}10`, color: A }} data-testid={`copy-code-${c.id}`}><Hash className="w-3 h-3" />{c.class_code || '-'} <ClipboardCopy className="w-2.5 h-2.5" /></button>
                          </div>
                          <h4 className="text-sm font-bold text-gray-900">{c.name}</h4>
                          <p className="text-xs text-gray-400 mt-0.5 font-mono">{c.code}</p>
                          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-50 flex-wrap">
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ background: `${A}08`, color: A }}><GraduationCap className="w-3 h-3" />{c.student_count || 0}</span>
                            {(c.pending_count || 0) > 0 && <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-[10px] font-semibold text-amber-700"><AlertCircle className="w-3 h-3" />{c.pending_count} pending</span>}
                          </div>
                          <div className="flex items-center gap-1 text-xs font-medium mt-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: A }}>Manage <ChevronRight className="w-3 h-3" /></div>
                        </div>
                      </Card>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
          {/* ═══ SELECTED CLASS DETAIL ═══ */}
          {selectedClass && (section === 'classes' || section === 'overview') && (
            <div className="space-y-5" data-testid="class-detail">
              {/* Class header */}
              <Card>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${A}12` }}>
                      <BookOpen className="w-5 h-5" style={{ color: A }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selectedClass.name}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Capacity: {selectedClass.capacity} &middot; <span className="font-mono">{selectedClass.code}</span></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl border relative group" style={{ background: `${A}06`, borderColor: `${A}25` }} data-testid="class-code-display">
                    <Hash className="w-4 h-4" style={{ color: A }} />
                    <span className="text-sm font-bold font-mono tracking-wider" style={{ color: A }}>{selectedClass.class_code}</span>
                    <button onClick={() => copyCode(selectedClass.class_code)} className="ml-1 p-1 rounded hover:bg-white/60" data-testid="copy-class-code"><ClipboardCopy className="w-3.5 h-3.5" style={{ color: A }} /></button>
                    <div className="relative">
                      <button className="p-1 rounded hover:bg-white/60 text-gray-400 hover:text-gray-600 peer" data-testid="class-code-info"><Info className="w-3.5 h-3.5" /></button>
                      <div className="hidden peer-hover:block absolute right-0 top-full mt-1 w-56 bg-gray-900 text-white text-[11px] rounded-lg p-2.5 z-20 shadow-lg">
                        Share code <span className="font-mono font-bold">{selectedClass.class_code}</span> with students to let them self-enroll.
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Class Teacher */}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white">
                <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {selectedClass.assigned_teacher_id ? (
                  <>
                    <button onClick={() => setTeacherInfoPopup({ name: selectedClass.assigned_teacher_name, email: selectedClass.assigned_teacher_email, id: selectedClass.assigned_teacher_id, assigned_at: selectedClass.teacher_assigned_at })} className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-1.5 py-1 -ml-1.5 transition-colors" data-testid="teacher-profile-click">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: A }}>{(selectedClass.assigned_teacher_name || '?')[0]}</div>
                      <div className="text-left"><p className="text-xs font-bold text-gray-900">{selectedClass.assigned_teacher_name}</p><p className="text-[10px] text-gray-500">{selectedClass.assigned_teacher_email}</p></div>
                    </button>
                    <div className="flex-1" />
                    <span className="text-[9px] font-semibold uppercase px-2 py-0.5 rounded-full bg-green-50 text-green-600">Class Teacher</span>
                    <Button onClick={() => { setShowSubModal(true); setSubStep(1); setSubSelected(null); setSubQuery(''); setSubResults([]); setSubStartDate(''); setSubEndDate(''); setSubReason(''); setSubConfirmPwd(''); setSubError(''); }} size="sm" className="h-7 px-3 rounded-lg text-[11px] border-blue-200 text-blue-600 hover:bg-blue-50" variant="outline" data-testid="assign-substitute-btn"><Shield className="w-3 h-3 mr-1" /> Assign Substitute</Button>
                    <button onClick={() => { setShowUnassign2FA(true); setUnassignPwd(''); }} className="text-[10px] text-red-400 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50" data-testid="unassign-class-teacher">Remove</button>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-gray-500 flex-1">No class teacher assigned</span>
                    <Button onClick={() => { setShowClassTeacherModal(true); setCtQuery(''); setCtResults([]); }} size="sm" className="h-7 px-3 rounded-lg text-white text-[11px]" style={{ background: A }} data-testid="assign-class-teacher-btn"><UserPlus className="w-3 h-3 mr-1" /> Assign Teacher</Button>
                    <Button onClick={() => { setShowSubModal(true); setSubStep(1); setSubSelected(null); setSubQuery(''); setSubResults([]); setSubStartDate(''); setSubEndDate(''); setSubReason(''); setSubConfirmPwd(''); setSubError(''); }} size="sm" className="h-7 px-3 rounded-lg text-[11px] border-blue-200 text-blue-600 hover:bg-blue-50" variant="outline" data-testid="assign-substitute-btn"><Shield className="w-3 h-3 mr-1" /> Assign Substitute</Button>
                  </>
                )}
              </div>

              {/* Active Class Substitutions */}
              {classSubs.filter(s => s.status === 'active').length > 0 && (
                <div className="space-y-2" data-testid="class-substitutes">
                  {classSubs.filter(s => s.status === 'active').map(sub => (
                    <div key={sub.id} className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid={`class-sub-${sub.id}`}>
                      <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white bg-blue-500">{(sub.substitute_teacher_name || '?')[0]}</div>
                          <p className="text-xs font-bold text-gray-900">{sub.substitute_teacher_name}</p>
                          <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-blue-500 text-white">Substitute</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5 ml-8">
                          <Calendar className="w-3 h-3 inline mr-1" />{sub.start_date} &mdash; {sub.end_date}
                          {sub.reason && <span className="ml-2 text-gray-400">&middot; {sub.reason}</span>}
                        </p>
                      </div>
                      <span className="text-[9px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded font-medium">{sub.lectures_posted || 0} lectures</span>
                      <button onClick={() => { setShowUnassignSub2FA(sub); setUnassignSubPwd(''); }} className="text-[10px] text-red-400 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50" data-testid={`unassign-sub-${sub.id}`}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Past Class Substitutions */}
              {classSubs.filter(s => s.status !== 'active').length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">Past Substitutions</p>
                  {classSubs.filter(s => s.status !== 'active').map(sub => (
                    <div key={sub.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 opacity-60" data-testid={`past-class-sub-${sub.id}`}>
                      <Shield className="w-3 h-3 text-gray-400" />
                      <p className="text-[10px] text-gray-600 flex-1">{sub.substitute_teacher_name} &middot; {sub.start_date} &mdash; {sub.end_date}</p>
                      <span className="text-[9px] text-gray-400 px-1.5 py-0.5 rounded bg-gray-100">{sub.status}</span>
                      <button onClick={async () => { try { await apiOrThrow(`/class-substitutions/${sub.id}`, { method: 'DELETE' }); } catch (e) { notifyError(e.data, 'Could not delete'); } loadClassSubs(selectedClass.id); }} className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500" data-testid={`delete-past-sub-${sub.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Unassign 2FA Confirmation */}
              {showUnassign2FA && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={() => setShowUnassign2FA(false)} />
                  <div className="relative bg-white rounded-2xl w-[360px] shadow-2xl border border-gray-100 p-5" data-testid="unassign-2fa-modal">
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50"><Lock className="w-4 h-4 text-red-500" /></div>
                      <div><h3 className="text-sm font-bold text-gray-900">Confirm Removal</h3><p className="text-[10px] text-gray-500">Remove {selectedClass.assigned_teacher_name} as class teacher</p></div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Your Password</label>
                      <Input type="password" value={unassignPwd} onChange={e => setUnassignPwd(e.target.value)} placeholder="Enter your password to confirm" className="h-9 rounded-lg text-sm" autoFocus data-testid="unassign-pwd" onKeyDown={e => e.key === 'Enter' && unassignPwd && document.querySelector('[data-testid="confirm-unassign"]')?.click()} />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={async () => {
                        if (!unassignPwd) return;
                        setUnassigning(true);
                        try {
                          const authRes = await verifyMyPassword(unassignPwd);
                          if (!authRes.ok) { notifyError(await authRes.json().catch(() => ({})), "Couldn't check your password."); setUnassigning(false); return; }
                          await apiOrThrow(`/classes/${selectedClass.id}/unassign-teacher`, { method: 'POST' });
                          await reloadSelectedClass();
                          setShowUnassign2FA(false);
                          toast.success('Teacher removed');
                        } catch (err) { toast.error(err.message || 'Could not remove the teacher'); }
                        setUnassigning(false);
                      }} disabled={unassigning || !unassignPwd} className="flex-1 h-9 rounded-lg text-white text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-50" data-testid="confirm-unassign">{unassigning ? 'Removing...' : 'Confirm & Remove'}</Button>
                      <button onClick={() => setShowUnassign2FA(false)} className="text-xs text-gray-500 px-3">Cancel</button>
                    </div>
                  </div>
                </div>
              )}


              {/* ═══ Assign Substitute Modal (3-step: search → dates → 2FA) ═══ */}
              {showSubModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowSubModal(false)} />
                  <div className="relative bg-white rounded-2xl p-6 w-[500px] shadow-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="class-sub-modal">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Shield className="w-5 h-5 text-blue-600" /></div>
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-gray-900">Assign Substitute Teacher</h3>
                        <p className="text-[11px] text-gray-500">Step {subStep} of 3 &middot; {subStep === 1 ? 'Select Teacher' : subStep === 2 ? 'Set Period' : 'Confirm (2FA)'}</p>
                      </div>
                      <button onClick={() => setShowSubModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
                    </div>
                    <div className="flex gap-1 mb-5">{[1,2,3].map(s => (<div key={s} className="h-1 flex-1 rounded-full transition-all" style={{ background: s <= subStep ? '#3B82F6' : '#E5E7EB' }} />))}</div>

                    {subStep === 1 && (
                      <>
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input value={subQuery} onChange={e => {
                            setSubQuery(e.target.value);
                            setSubSearching(true);
                            const tk = localStorage.getItem('desky_token');
                            fetch(`${API_URL}/api/teachers/search?q=${encodeURIComponent(e.target.value)}`, { headers: { Authorization: `Bearer ${tk}` } }).then(r => r.json()).then(d => { setSubResults(d || []); setSubSearching(false); }).catch(() => setSubSearching(false));
                          }} placeholder="Search teacher by name or email..." className="h-10 pl-10 rounded-xl" autoFocus data-testid="class-sub-search" />
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-1 min-h-[150px] max-h-[280px]">
                          {subSearching ? <Loader />
                          : subResults.map(t => (
                            <button key={t.id} onClick={() => { setSubSelected(t); setSubStep(2); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-blue-50 text-left transition-colors" data-testid={`class-sub-teacher-${t.id}`}>
                              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">{(t.full_name || '?')[0]}</div>
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-900">{t.full_name}</p>
                                <p className="text-[11px] text-gray-500">{t.email} &middot; <span className="font-mono text-gray-400">{t.id}</span></p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-300" />
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {subStep === 2 && subSelected && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
                          <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-800">{(subSelected.full_name || '?')[0]}</div>
                          <div className="flex-1"><p className="text-sm font-bold text-gray-900">{subSelected.full_name}</p><p className="text-[11px] text-gray-500">{subSelected.email}</p></div>
                          <button onClick={() => { setSubSelected(null); setSubStep(1); }} className="p-1 rounded-lg hover:bg-blue-100 text-blue-400"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">Start Date *</label>
                            <Input type="date" value={subStartDate} onChange={e => setSubStartDate(e.target.value)} className="h-10 rounded-xl text-sm" data-testid="class-sub-start" /></div>
                          <div><label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">End Date *</label>
                            <Input type="date" value={subEndDate} onChange={e => setSubEndDate(e.target.value)} className="h-10 rounded-xl text-sm" data-testid="class-sub-end" /></div>
                        </div>
                        <div><label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">Reason (optional)</label>
                          <Input value={subReason} onChange={e => setSubReason(e.target.value)} placeholder="e.g., Teacher on leave" className="h-10 rounded-xl text-sm" data-testid="class-sub-reason" /></div>
                        <Button onClick={() => setSubStep(3)} disabled={!subStartDate || !subEndDate} className="w-full h-10 rounded-xl text-white text-sm font-medium active:scale-95 disabled:opacity-50 bg-blue-600 hover:bg-blue-700" data-testid="class-sub-next">Continue to Verification</Button>
                      </div>
                    )}

                    {subStep === 3 && subSelected && (
                      <div className="space-y-4">
                        <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-800">{(subSelected.full_name || '?')[0]}</div>
                            <div><p className="text-sm font-bold text-gray-900">{subSelected.full_name}</p><p className="text-[10px] text-gray-500">{subSelected.email}</p></div>
                          </div>
                          <p className="text-[11px] text-gray-600 ml-12"><Calendar className="w-3 h-3 inline mr-1" />{subStartDate} &mdash; {subEndDate}{subReason && <span className="ml-2 text-gray-400">&middot; {subReason}</span>}</p>
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Your Password (2FA)</label>
                          <Input type="password" value={subConfirmPwd} onChange={e => setSubConfirmPwd(e.target.value)} placeholder="Enter your password to confirm" className="h-10 rounded-xl" autoFocus data-testid="class-sub-pwd" onKeyDown={e => {
                            if (e.key === 'Enter') document.querySelector('[data-testid="class-sub-confirm"]')?.click();
                          }} />
                        </div>
                        {subError && <p className="text-xs text-red-500">{subError}</p>}
                        <div className="flex gap-2">
                          <button onClick={() => setSubStep(2)} className="h-10 px-4 rounded-xl text-sm text-gray-500 hover:bg-gray-50 border border-gray-200">Back</button>
                          <Button onClick={async () => {
                            if (!subConfirmPwd || !subSelected) return;
                            setSubAssigning(true); setSubError('');
                            try {
                              const authRes = await verifyMyPassword(subConfirmPwd);
                              if (!authRes.ok) { setSubError(friendlyError(await authRes.json().catch(() => ({})), "Couldn't check your password.")); setSubAssigning(false); return; }
                              const tk = localStorage.getItem('desky_token');
                              const r = await fetch(`${API_URL}/api/classes/${selectedClass.id}/assign-substitute`, {
                                method: 'POST', headers: { Authorization: `Bearer ${tk}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ substitute_teacher_id: subSelected.id, start_date: subStartDate, end_date: subEndDate, reason: subReason }),
                              });
                              if (!r.ok) { const d = await r.json().catch(() => ({})); setSubError(friendlyError(d, 'Failed')); setSubAssigning(false); return; }
                              setShowSubModal(false);
                              toast.success(`${subSelected.full_name} assigned as substitute`);
                              loadClassSubs(selectedClass.id);
                            } catch { setSubError('Assignment failed'); }
                            setSubAssigning(false);
                          }} disabled={subAssigning || !subConfirmPwd} className="flex-1 h-10 rounded-xl text-white text-sm font-medium active:scale-95 disabled:opacity-50 bg-blue-600 hover:bg-blue-700" data-testid="class-sub-confirm">
                            {subAssigning ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Verifying...</> : <><Lock className="w-4 h-4 mr-2" /> Confirm &amp; Assign</>}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ═══ Unassign Substitute 2FA ═══ */}
              {showUnassignSub2FA && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={() => setShowUnassignSub2FA(null)} />
                  <div className="relative bg-white rounded-2xl w-[400px] shadow-2xl border border-gray-100 p-5" data-testid="unassign-sub-2fa-modal">
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-50"><Shield className="w-4 h-4 text-red-500" /></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">Unassign Substitute</h3>
                        <p className="text-[10px] text-gray-500">Remove {showUnassignSub2FA.substitute_teacher_name} from substitution</p>
                      </div>
                    </div>
                    <div className="bg-blue-50/50 rounded-xl p-3 mb-4 border border-blue-100">
                      <p className="text-[11px] font-semibold text-gray-900">{showUnassignSub2FA.substitute_teacher_name}</p>
                      <p className="text-[10px] text-gray-500">{showUnassignSub2FA.start_date} &mdash; {showUnassignSub2FA.end_date}</p>
                    </div>
                    <div className="mb-4">
                      <label className="block text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Your Password (2FA)</label>
                      <Input type="password" value={unassignSubPwd} onChange={e => setUnassignSubPwd(e.target.value)} placeholder="Enter password to confirm" className="h-9 rounded-lg text-sm" autoFocus data-testid="unassign-sub-pwd" onKeyDown={e => e.key === 'Enter' && document.querySelector('[data-testid="confirm-unassign-sub"]')?.click()} />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={async () => {
                        if (!unassignSubPwd) return;
                        setUnassigningSub(true);
                        try {
                          const tk = localStorage.getItem('desky_token');
                          const res = await fetch(`${API_URL}/api/class-substitutions/${showUnassignSub2FA.id}/unassign`, {
                            method: 'POST', headers: { Authorization: `Bearer ${tk}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ password: unassignSubPwd }),
                          });
                          if (!res.ok) { notifyError(await res.json().catch(() => ({})), "Couldn't remove the substitute."); setUnassigningSub(false); return; }
                          setShowUnassignSub2FA(null);
                          toast.success('Substitute removed');
                          loadClassSubs(selectedClass.id);
                        } catch (e) { notifyError(e, "Couldn't remove the substitute."); }
                        setUnassigningSub(false);
                      }} disabled={unassigningSub || !unassignSubPwd} className="flex-1 h-9 rounded-lg text-white text-xs font-semibold bg-red-500 hover:bg-red-600 disabled:opacity-50" data-testid="confirm-unassign-sub">{unassigningSub ? 'Removing...' : 'Confirm & Unassign'}</Button>
                      <button onClick={() => setShowUnassignSub2FA(null)} className="text-xs text-gray-500 px-3">Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Teacher Info Popup */}
              {teacherInfoPopup && (
                <ProfileCard
                  person={{ full_name: teacherInfoPopup.name, email: teacherInfoPopup.email, id: teacherInfoPopup.id, assigned_at: teacherInfoPopup.assigned_at, phone: teacherInfoPopup.phone }}
                  accent={A}
                  role="Class Teacher"
                  onClose={() => setTeacherInfoPopup(null)}
                />
              )}
              {/* Class Teacher Search Modal */}
              {showClassTeacherModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" onClick={() => setShowClassTeacherModal(false)} />
                  <div className="relative bg-white rounded-2xl w-[380px] shadow-2xl border border-gray-100">
                    <div className="p-5 pb-3">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-gray-900">Assign Class Teacher</h3>
                        <button onClick={() => setShowClassTeacherModal(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
                      </div>
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input value={ctQuery} onChange={e => setCtQuery(e.target.value)} placeholder="Search by name or email..." className="h-9 pl-9 rounded-lg text-sm" autoFocus data-testid="ct-search" />
                      </div>
                    </div>
                    <div className="max-h-[220px] overflow-y-auto px-5 pb-4">
                      {ctSearching ? <p className="text-xs text-gray-400 text-center py-4">Searching...</p>
                      : ctResults.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">{ctQuery ? 'No results' : 'No teachers found'}</p>
                      : ctResults.map(t => (
                        <div key={t.id} className="flex items-center gap-3 py-2.5 hover:bg-gray-50 rounded-lg px-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: A }}>{(t.full_name || '?')[0]}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-gray-900">{t.full_name}</p>
                            <p className="text-[10px] text-gray-500">{t.email}</p>
                            <p className="text-[9px] text-gray-400 font-mono">ID: {t.id}</p>
                          </div>
                          <button onClick={async () => {
                            setCtAssigning(t.id);
                            try {
                              await apiOrThrow(`/classes/${selectedClass.id}/assign-teacher`, { method: 'POST', body: JSON.stringify({ teacher_id: t.id }) });
                              await reloadSelectedClass();
                              setShowClassTeacherModal(false);
                              toast.success(`${t.full_name} assigned`);
                            } catch (err) { toast.error(err.message || 'Assignment failed'); }
                            setCtAssigning(null);
                          }} disabled={ctAssigning === t.id} className="h-6 px-2.5 rounded text-[10px] font-semibold text-white" style={{ background: A }}>{ctAssigning === t.id ? '...' : 'Assign'}</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1" data-testid="class-tabs">
                {[
                  { id: 'students', label: 'Enrolled Students', icon: GraduationCap, count: classStudents.length },
                  { id: 'applied', label: 'Students Applied', icon: UserPlus, count: pendingEnrollments.length },
                  { id: 'subjects', label: 'Subjects & Teachers', icon: BookOpen, count: 0 },
                ].map(tab => (
                  <button key={tab.id} onClick={() => { setClassTab(tab.id); if (tab.id === 'applied') loadPendingEnrollments(selectedClass.id); }} data-testid={`tab-${tab.id}`}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${classTab === tab.id ? 'text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}
                    style={classTab === tab.id ? { background: A } : {}}>
                    <tab.icon className="w-4 h-4" /> {tab.label}
                    {(tab.count || 0) > 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${classTab === tab.id ? 'bg-white/20' : 'bg-gray-100'}`}>{tab.count}</span>}
                  </button>
                ))}
              </div>

              {/* Enrolled tab */}
              {classTab === 'students' && (
                <div className="space-y-4" data-testid="students-tab">
                  {selectedStudentProfile && <ProfileCard person={selectedStudentProfile} accent={A} role="Student" onClose={() => setSelectedStudentProfile(null)} />}
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-500">{classStudents.length} student{classStudents.length !== 1 ? 's' : ''}</p>
                    <div className="flex gap-2">
                      {[{ mode: 'email', icon: Mail, label: 'Email' },{ mode: 'available', icon: UserPlus, label: 'Pick' },{ mode: 'csv', icon: Upload, label: 'CSV' }].map(m => (
                        <Button key={m.mode} variant={addMode === m.mode ? 'default' : 'outline'} onClick={() => { setAddMode(addMode === m.mode ? null : m.mode); if (m.mode === 'available') loadAvailableStudents(selectedClass.id); }}
                          className={`h-8 px-3 rounded-lg text-xs font-semibold ${addMode === m.mode ? 'text-white' : ''}`} style={addMode === m.mode ? { background: A } : {}} data-testid={`add-${m.mode}-btn`}>
                          <m.icon className="w-3 h-3 mr-1" /> {m.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Student search */}
                  {classStudents.length > 0 && !addMode && (
                    <Card className="!p-3" data-testid="class-student-search">
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1 min-w-[180px]">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input value={classStudentSearch} onChange={e => setClassStudentSearch(e.target.value)} placeholder="Search enrolled students..." className="h-9 pl-9 rounded-lg text-sm" data-testid="class-student-search-input" />
                        </div>
                        <span className="text-[10px] text-gray-400">{filteredClassStudents.length} of {classStudents.length}</span>
                      </div>
                    </Card>
                  )}

                  {addMode === 'email' && (
                    <Card className="border-2 border-dashed" style={{ borderColor: `${A}40` }} data-testid="add-email-form">
                      <p className="text-xs text-gray-400 mb-2">Enter registered student emails (comma or newline separated)</p>
                      <textarea value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="student1@school.edu, student2@school.edu" className="w-full h-20 rounded-lg border border-gray-200 text-sm p-3 resize-none" data-testid="email-textarea" />
                      <div className="flex gap-2 mt-2">
                        <Button onClick={addStudentsByEmail} disabled={enrollLoading} className="h-8 px-4 rounded-lg text-white text-xs font-semibold" style={{ background: A }} data-testid="submit-emails-btn">{enrollLoading ? 'Adding...' : 'Add'}</Button>
                        <Button variant="ghost" onClick={() => { setAddMode(null); setEmailInput(''); }} className="h-8 text-xs">Cancel</Button>
                      </div>
                    </Card>
                  )}

                  {addMode === 'available' && (
                    <Card className="border-2 border-dashed" style={{ borderColor: `${A}40` }} data-testid="add-available-form">
                      {availableStudents.length === 0 ? <p className="text-xs text-gray-400 py-2">No available students</p> : (
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {availableStudents.map(s => {
                            const sel = selectedAvailable.some(x => x.id === s.id);
                            return (
                              <button key={s.id} onClick={() => setSelectedAvailable(prev => sel ? prev.filter(x => x.id !== s.id) : [...prev, s])}
                                className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${sel ? 'border' : 'hover:bg-gray-50'}`} style={sel ? { background: `${A}06`, borderColor: `${A}30` } : {}} data-testid={`avail-${s.id}`}>
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center`} style={sel ? { borderColor: A, background: A } : { borderColor: '#D1D5DB' }}>
                                  {sel && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                                </div>
                                <span className="text-sm text-gray-900">{s.full_name}</span>
                                <span className="text-xs text-gray-400 ml-auto">{s.email}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {selectedAvailable.length > 0 && (
                        <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                          <Button onClick={addSelectedStudents} disabled={enrollLoading} className="h-8 px-4 rounded-lg text-white text-xs font-semibold" style={{ background: A }} data-testid="submit-available-btn">{enrollLoading ? '...' : `Add ${selectedAvailable.length}`}</Button>
                          <Button variant="ghost" onClick={() => { setAddMode(null); setSelectedAvailable([]); }} className="h-8 text-xs">Cancel</Button>
                        </div>
                      )}
                    </Card>
                  )}

                  {addMode === 'csv' && (
                    <Card className="border-2 border-dashed" style={{ borderColor: `${A}40` }} data-testid="add-csv-form">
                      <p className="text-xs text-gray-400 mb-2">Paste CSV with student emails</p>
                      <textarea value={csvInput} onChange={e => setCsvInput(e.target.value)} placeholder="name,email&#10;John,john@school.edu" className="w-full h-24 rounded-lg border border-gray-200 text-sm p-3 font-mono resize-none" data-testid="csv-textarea" />
                      <div className="flex gap-2 mt-2">
                        <Button onClick={addStudentsByCSV} disabled={enrollLoading} className="h-8 px-4 rounded-lg text-white text-xs font-semibold" style={{ background: A }} data-testid="submit-csv-btn">{enrollLoading ? '...' : 'Import'}</Button>
                        <Button variant="ghost" onClick={() => { setAddMode(null); setCsvInput(''); }} className="h-8 text-xs">Cancel</Button>
                      </div>
                    </Card>
                  )}

                  {openingClass ? <Loader label="Loading students…" /> : classStudents.length === 0 && !addMode ? (
                    <Card className="text-center py-12 border-2 border-dashed">
                      <GraduationCap className="w-10 h-10 mx-auto mb-2" style={{ color: `${A}30` }} />
                      <p className="text-sm text-gray-400">No students enrolled yet</p>
                    </Card>
                  ) : classStudents.length > 0 && (
                    <Card className="overflow-hidden !p-0" data-testid="enrolled-students-list">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50/80 border-b border-gray-100">
                          <tr>
                            <th className="text-left px-5 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Student</th>
                            <th className="text-left px-5 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="text-right px-5 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredClassStudents.map(s => (
                            <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors" data-testid={`student-row-${s.student_id}`}>
                              <td className="px-5 py-3.5">
                                <button onClick={() => setSelectedStudentProfile({ full_name: s.student_name, email: s.student_email, student_id: s.student_id, ...s })} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid={`student-click-${s.student_id}`}>
                                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: `${A}cc` }}>{(s.student_name || '?')[0].toUpperCase()}</div>
                                  <span className="font-medium text-gray-900">{s.student_name}</span>
                                </button>
                              </td>
                              <td className="px-5 py-3.5 text-gray-500">{s.student_email}</td>
                              <td className="px-5 py-3.5">
                                <div className="flex items-center justify-end gap-2">
                                  <button onClick={() => removeStudent(s.student_id)} className="text-xs text-red-400 hover:text-red-600" data-testid={`remove-student-${s.student_id}`}>Remove</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredClassStudents.length === 0 && classStudents.length > 0 && (
                        <div className="text-center py-8">
                          <p className="text-sm text-gray-400">No students match your search</p>
                        </div>
                      )}
                    </Card>
                  )}
                </div>
              )}

              {/* Applied tab */}
              {classTab === 'applied' && (
                <div className="space-y-4" data-testid="applied-tab">
                  {pendingEnrollments.length === 0 ? (
                    <Card className="text-center py-12 border-2 border-dashed">
                      <UserPlus className="w-10 h-10 mx-auto mb-2 text-amber-300" />
                      <p className="text-sm text-gray-500">No pending applications</p>
                      <p className="text-xs text-gray-400 mt-1">Students can apply using code <span className="font-mono font-bold">{selectedClass.class_code}</span></p>
                    </Card>
                  ) : pendingEnrollments.map(e => (
                    <Card key={e.id} className="flex items-center gap-4" data-testid={`applied-${e.id}`}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white bg-amber-400">{(e.student_name || '?')[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900">{e.student_name}</p>
                        <p className="text-xs text-gray-400">{e.student_email}{e.requested_at && ` · Applied ${new Date(e.requested_at).toLocaleDateString()}`}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleEnrollmentAction(selectedClass.id, e.id, 'approve')} className="h-8 px-4 rounded-lg text-xs font-semibold text-white" style={{ background: '#22C55E' }} data-testid={`approve-${e.id}`}><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                        <Button onClick={() => handleEnrollmentAction(selectedClass.id, e.id, 'reject')} variant="outline" className="h-8 px-4 rounded-lg text-xs font-semibold text-red-500 border-red-200" data-testid={`reject-${e.id}`}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Subjects & Teachers Tab */}
              {classTab === 'subjects' && (
                <ClassSubjectsTab classId={selectedClass.id} accent={A} />
              )}
            </div>
          )}

          {/* ═══ TEACHER DESK ═══ */}
          {section === 'teacher-desk' && (
            <TeacherDeskSection accent={A} isDepartment={true} />
          )}

          {/* ═══ DESKEY HUB ═══ */}
          {section === 'deskey-hub' && (
            <DeskeyHubSection accent={A} mode="department" />
          )}

          {/* ═══ TEACHERS (read-only) ═══ */}
          {section === 'teachers' && (
            <TeachersSection teachers={deptTeachers} accent={A} readOnly data-testid="dept-teachers-section" />
          )}

          {/* ═══ ALL STUDENTS (read-only) ═══ */}
          {section === 'students' && (
            <StudentsSection students={allStudents.map(s => ({
              ...s,
              id: s.student_id, full_name: s.student_name, email: s.student_email,
              department_names: dept.name, departments: [{ id: dept.id, name: dept.name }],
              enrolled_classes: (s.class_ids || []).length,
            }))} accent={A} readOnly data-testid="dept-students-section" />
          )}

          {/* ═══ ENROLLMENT REQUESTS ═══ — Enrollment Requests page hidden for now
          {section === 'enrollments' && (
            <div className="space-y-5" data-testid="enrollment-requests-section">
              <p className="text-sm text-gray-400">{allPending.length} pending request{allPending.length !== 1 ? 's' : ''} across all classes</p>
              {allPending.length === 0 ? (
                <Card className="text-center py-16">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-sm font-medium text-gray-500">All caught up!</p>
                  <p className="text-xs text-gray-400 mt-1">No pending enrollment requests</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {allPending.map(e => (
                    <Card key={e.id} className="flex items-center gap-4" data-testid={`pending-${e.id}`}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white bg-amber-400">{(e.student_name || '?')[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900">{e.student_name}</p>
                        <p className="text-xs text-gray-400">{e.student_email}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-md font-medium" style={{ background: `${A}08`, color: A }}>{e.class_name}</span>
                      {e.requested_at && <span className="text-[10px] text-gray-400">{new Date(e.requested_at).toLocaleDateString()}</span>}
                      <div className="flex gap-2">
                        <Button onClick={() => handleEnrollmentAction(e.class_id, e.enrollment_id, 'approve')} className="h-8 px-4 rounded-lg text-xs font-semibold text-white" style={{ background: '#22C55E' }} data-testid={`approve-all-${e.id}`}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button onClick={() => handleEnrollmentAction(e.class_id, e.enrollment_id, 'reject')} variant="outline" className="h-8 px-4 rounded-lg text-xs font-semibold text-red-500 border-red-200" data-testid={`reject-all-${e.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
          */}

          {/* ═══ SETTINGS ═══ */}
          {section === 'settings' && (
            <div className="space-y-4" data-testid="dept-settings-section">
              {/* Change Password — step 1 emails a code, step 2 sets the new password */}
              <Card data-testid="password-settings">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#7C3AED14' }}><Lock className="w-3.5 h-3.5 text-violet-600" /></div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-gray-900">Change Password</h3>
                    <p className="text-[11px] text-gray-500">Forgot it? Ask your institution admin to reset it from Departments → Manage Access.</p>
                  </div>
                </div>

                {!pwdCodeSent ? (
                  <div className="max-w-xl">
                    <p className="text-xs text-gray-600 mb-3">For security, we'll first email a 6-digit verification code to <span className="font-semibold text-gray-800">{user?.email}</span>.</p>
                    <Button onClick={sendPasswordCode} disabled={sendingPwdCode} className="h-9 px-5 rounded-lg text-white text-xs font-semibold disabled:opacity-50" style={{ background: A }} data-testid="send-password-code-btn">
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> {sendingPwdCode ? 'Sending...' : 'Send Verification Code'}
                    </Button>
                  </div>
                ) : (
                  <div className="max-w-xl space-y-3">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Verification code (sent to {user?.email})</label>
                      <Input value={pwdCode} onChange={e => setPwdCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className="h-9 w-40 rounded-lg text-center text-sm font-mono font-bold tracking-widest" autoFocus data-testid="password-code-input" />
                      <button type="button" onClick={sendPasswordCode} disabled={sendingPwdCode} className="text-[11px] font-medium mt-1 hover:underline disabled:opacity-50" style={{ color: A }} data-testid="resend-password-code">{sendingPwdCode ? 'Sending...' : 'Resend code'}</button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { label: 'Current password', value: oldPassword, set: setOldPassword, show: showOld, toggle: setShowOld, testId: 'current-password' },
                        { label: `New password (min ${MIN_PASSWORD_LENGTH} characters)`, value: newPassword, set: setNewPassword, show: showNew, toggle: setShowNew, testId: 'new-password' },
                      ].map(f => (
                        <div key={f.testId}>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{f.label}</label>
                          <div className="relative">
                            <Input type={f.show ? 'text' : 'password'} value={f.value} onChange={e => f.set(e.target.value)} className="h-9 rounded-lg text-sm pr-9" data-testid={f.testId} />
                            <button type="button" onClick={() => f.toggle(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600" aria-label={f.show ? 'Hide password' : 'Show password'}>
                              {f.show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Button onClick={changePassword} disabled={changingPwd || pwdCode.length !== 6 || !oldPassword || newPassword.length < MIN_PASSWORD_LENGTH} className="h-9 px-5 rounded-lg text-white text-xs font-semibold disabled:opacity-50" style={{ background: A }} data-testid="change-password-btn">
                        {changingPwd ? 'Saving...' : 'Change Password'}
                      </Button>
                      <Button variant="ghost" onClick={resetPasswordForm} className="h-9 px-4 rounded-lg text-xs text-gray-500">Cancel</Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
